import React, { useState } from 'react';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import { OrderProduct, ProductApi, PricingTableObject, PurchaseTypes } from 'common/types';
import { Theme } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import PlusIcon from '@mui/icons-material/Add';
import MinusIcon from '@mui/icons-material/Remove';
import Typography from '@mui/material/Typography';
import {
	createOrderProductFromProductApi,
	PartialOrderProductFields,
	newFirestoreId,
	OrderProductCreation,
} from 'common/utils/newRentalUtils';
import { getLanguage } from 'services/localization/utils';
import { getTranslation } from 'common/modules/translations';
import { useTranslation } from 'services/localization/useTranslation';
import { useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { getDefaultTimezone } from 'common/utils/dateUtils';

interface OwnProps {
	open: boolean;
	onClose: (products: OrderProduct[]) => void;
	products: ProductApi[];
	activeProducts: OrderProduct[];
	partialOrderProductFields: PartialOrderProductFields;
	savedPricingTables?: PricingTableObject;
	currencyCode: string;
	taxExcluded: boolean;
}

type Props = OwnProps & WithStyles<typeof styles>;

const AdditionalProductUnitsDialog = (props: Props) => {
	const [activeProducts, setActiveProducts] = useState<OrderProduct[]>([...props.activeProducts]);
	const shopTimezone = useSelector(ShopSelectors.getShopTimeZone) ?? getDefaultTimezone();
	const openingHours = useSelector(ShopSelectors.activeLocationOpeningHoursWithException);

	const { t } = useTranslation();

	const { products, classes } = props;

	const handleCancel = () => {
		props.onClose(props.activeProducts);
	};

	const handleOk = () => {
		props.onClose(activeProducts);
	};

	const removeProduct = (productId: string) => {
		const newProducts = activeProducts.filter((p) => p.id !== productId);

		setActiveProducts(newProducts);
	};

	const addProduct = (product: ProductApi) => {
		const { products, partialOrderProductFields, currencyCode, taxExcluded } = props;
		const selectedVariant = product.variants.options[0]; // For now just takes the first variantId to make sure availability-calculations take into account this. Axel Cedercreutz 9th of July 2021
		const partialOrderProductFieldsWithNewId: PartialOrderProductFields = {
			...partialOrderProductFields,
			id: newFirestoreId(),
		};
		const orderProductCreationObject: OrderProductCreation = {
			stockProduct: product,
			stockProductListForSetProducts: products,
			savedPricingTables: props.savedPricingTables || {},
			partialOrderProductFields: partialOrderProductFieldsWithNewId,
			currencyCode,
			taxExcluded,
			selectedVariant,
			productVariantIds: [selectedVariant.id],
			segment: null,
			channel: 'ADMIN',
			purchaseType: PurchaseTypes.rental,
			timezone: shopTimezone,
			openingHours,
		};
		const newOrderProduct = createOrderProductFromProductApi(orderProductCreationObject);
		setActiveProducts([...activeProducts, newOrderProduct]);
	};

	const renderAdditionalProducts = (products: ProductApi[]) => {
		const classes = props.classes;
		return products.map((product, index) => {
			const activeProductsForSelectedProduct = activeProducts.filter(
				(p) => p.productApiId === product.id,
			);
			const name = getTranslation(product.name, getLanguage());
			const units = activeProductsForSelectedProduct.length;
			const lastSelectedProduct =
				activeProductsForSelectedProduct[activeProductsForSelectedProduct.length - 1] ?? null;
			return (
				<div className={classes.productSelection} key={index}>
					<Button
						className={classes.addButton}
						variant="outlined"
						onClick={() =>
							!!units && !!lastSelectedProduct ? removeProduct(lastSelectedProduct.id) : undefined
						}
					>
						<MinusIcon color="primary" />
					</Button>
					<Typography className={classes.addUnit} variant="body2" align="center">
						<b>{units ? `${units} x ` : ''}</b> {name}
					</Typography>
					<Button
						className={classes.addButton}
						variant="outlined"
						onClick={() => addProduct(product)}
					>
						<PlusIcon color="primary" />
					</Button>
				</div>
			);
		});
	};

	return (
		<Dialog
			disableEscapeKeyDown
			maxWidth="xs"
			aria-labelledby="confirmation-dialog-title"
			open={props.open}
			classes={{
				paper: classes.productDialogPaper,
			}}
		>
			<DialogTitle id="confirmation-dialog-title">{t('product.additionalProducts')}</DialogTitle>
			<DialogContent>{renderAdditionalProducts(products)}</DialogContent>
			<DialogActions>
				<Button onClick={handleCancel} color="secondary">
					{t('common:actions.cancel')}
				</Button>
				<Button onClick={handleOk} color="primary" variant="contained">
					{t('newRental.addToOrder')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		productSelection: {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			padding: 8,
		},
		addButton: {
			padding: 8,
			flex: '0 0 auto',
			minWidth: 0,
		},
		addUnit: {
			flex: '1 0 auto',
			minWidth: 48,
		},
		productDialogPaper: {
			width: '80%',
			maxHeight: 435,
		},
	});

export default withStyles(styles)(AdditionalProductUnitsDialog);
